import React, { useEffect } from 'react';
import { Layout } from '@/components/Common';
import CustomHead from '@/components/Common/Layout/Head';
import { NextPageWithLayout } from '@/pages/_app';
import type { GetServerSidePropsContext, InferGetStaticPropsType } from 'next';
import Script from 'next/script';
import { WEBSITE_URL } from '@/shared/constants';
import { businessSettingsDisplay, logPageView } from '@/lib/service';
import { BusinessSettingConfigKey } from '@/shared/types/business-setting';
import { CategoryConfig } from '@/shared/types/category';
import { ViewType } from '@/shared/types/sourcing-marketplace';
import { getMatchingCategoryAndVertical } from '@/shared/utils/sourcing-marketplace-util';
import BrowseSourcingLayout from '@/components/Common/Layout/BrowseSourcingLayout';
import CustomProjectPage from '@/components/SourcingMarketplace/BrowseSourcing/CustomProjectPage';
import { getBreadcrumbListJsonLd } from '@/shared/seo/jsonld';
import s3ImageTransform from '@/shared/utils/s3ImageTransform';
import { isMobileByUserAgent } from '@/shared/utils/utils';

export async function getServerSideProps(context: GetServerSidePropsContext) {
  try {
    const userAgent = context.req.headers['user-agent'];
    const isMobile = isMobileByUserAgent(userAgent);
    const categoryHandle = context.params.category as string;
    const response = await businessSettingsDisplay(BusinessSettingConfigKey.CREATOR_HUB_HOMEPAGE_CATEGORIES_V2);
    const categoryConfig = response?.value as { [key: string]: CategoryConfig[] };

    const { category, vertical } = getMatchingCategoryAndVertical(categoryHandle, categoryConfig);

    if (!category) {
      return { notFound: true };
    }
    context.res.setHeader('Cache-Control', 'public, s-maxage=60, stale-while-revalidate=300');
    return {
      props: {
        isMobile,
        category,
        vertical,
        categoryConfig,
      },
    };
  } catch (e) {
    return { notFound: true };
  }
}

const BrowseRFPPage: NextPageWithLayout<InferGetStaticPropsType<typeof getServerSideProps>> = ({ category }) => {
  useEffect(() => {
    if (category?.title) {
      logPageView({
        pageUrl: window.location.href || '',
        routeName: window.location.pathname,
        pageTitle: `${category.title} | Submit A Custom Project | Pietra`,
      });
    }
  }, [category.title]);
  return <CustomProjectPage />;
};

BrowseRFPPage.getLayout = function getLayout(page: React.ReactElement) {
  const { category, vertical, categoryConfig, isMobile } = page.props;

  return (
    <>
      <CustomHead
        title={`Submit A Custom Project | ${category.title} | Pietra`}
        description="Access to 1300+ trusted product, packaging, and merch manufacturers. Plus, software to scale your business while cutting costs."
        url={`${WEBSITE_URL}/sourcing/browse/${category.key}/rfp`}
        image={s3ImageTransform(category.image, 'grande')}
      />
      <Layout hideFooter>
        <BrowseSourcingLayout
          isMobile={isMobile}
          category={category}
          vertical={vertical}
          viewType={ViewType.RFP}
          categoryConfig={categoryConfig}
          categoryList={[]}
          searchCounts={{}}
        >
          {page}
        </BrowseSourcingLayout>
      </Layout>
      <Script
        id="product-jsonld"
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify([
            getBreadcrumbListJsonLd([{ id: `${WEBSITE_URL}/sourcing`, name: 'Browse all categories' }]),
          ]),
        }}
      />
    </>
  );
};

export default BrowseRFPPage;
